import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { revenueDashboard, revenueAction, fmtMoney, fmtNum } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import {
  TrendUp, CurrencyDollar, Package, Stack, Gift, Megaphone, EnvelopeSimple, ChartLineUp, Tag, Sparkle, ShieldCheck, ArrowRight, Storefront,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const KIND_ICON = { bundle: Gift, pricing: Tag, ads: Megaphone, promotion: TrendUp, email: EnvelopeSimple };

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="card-matte p-4" data-testid={`revenue-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 label-caps"><Icon size={13} className={accent ? "brand-text" : "text-[#9a9a9a]"} weight="bold" /> {label}</div>
      <div className="font-head text-xl md:text-2xl font-semibold mt-2">{value}</div>
    </div>
  );
}

export default function AiRevenueManagerPage() {
  const { activeId, activeClient } = useClient();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["revenue", activeId], queryFn: () => revenueDashboard(activeId), enabled: !!activeId });
  const d = q.data || {};
  const k = d.kpis || {};
  const f = d.forecast || {};

  const act = useMutation({
    mutationFn: (r) => revenueAction({ client_id: activeId, action: r.action, product_title: r.product_title }),
    onSuccess: () => { toast.success("Recommendation queued → Approval Queue (nothing changes Shopify automatically)"); qc.invalidateQueries({ queryKey: ["revenue", activeId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <PageContainer>
      <PageHeader eyebrow="AI Command" title="AI Revenue Manager"
        subtitle="An AI focused on growing revenue — bundles, upsells, pricing, promotions and campaigns, each with a clear WHY and expected impact. Every action is approval-gated.">
        <button data-testid="goto-approvals-rev" onClick={() => navigate("/approvals")} className="text-sm brand-text hover:text-[#A56CFF] flex items-center gap-1">Approval Queue <ArrowRight size={14} /></button>
      </PageHeader>

      {d.data_source === "empty" ? (
        <div className="card-matte p-10 text-center" data-testid="revenue-empty">
          <CurrencyDollar size={30} className="text-[#5a5a5a] mx-auto mb-3" />
          <div className="text-[#cfcfcf] font-semibold mb-1">No product revenue data for {activeClient?.name}</div>
          <div className="text-sm text-[#9a9a9a] mb-4">Connect & sync this workspace's Shopify store to unlock revenue recommendations.</div>
          <button data-testid="rev-connect-shopify" onClick={() => navigate("/shopify")} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><Storefront size={15} /> Connect Shopify</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6" data-testid="revenue-kpis">
            <Kpi label="Total Revenue" value={fmtMoney(k.total_revenue)} icon={CurrencyDollar} accent />
            <Kpi label="Avg Order Value" value={fmtMoney(k.aov)} icon={ChartLineUp} />
            <Kpi label="Best Sellers" value={fmtNum(k.best_sellers)} icon={TrendUp} accent />
            <Kpi label="Slow Movers" value={fmtNum(k.slow_movers)} icon={Package} />
            <Kpi label="Overstock" value={fmtNum(k.overstock)} icon={Stack} />
            <Kpi label="Opportunities" value={fmtNum(k.opportunities)} icon={Sparkle} accent />
          </div>

          <div className="card-matte p-5 mb-6" data-testid="revenue-forecast">
            <div className="label-caps mb-3 flex items-center gap-1"><ChartLineUp size={13} className="brand-text" /> 30-Day Revenue Forecast</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[["Conservative", f.next_30d_low], ["Base case", f.next_30d_base], ["Optimistic", f.next_30d_high], ["Uplift if actioned", f.uplift_if_actioned]].map(([l, v], i) => (
                <div key={l}><div className="text-[10px] uppercase text-[#7a7a7a]">{l}</div><div className={`font-head text-lg font-semibold ${i === 3 ? "brand-text" : ""}`}>{fmtMoney(v)}</div></div>
              ))}
            </div>
          </div>

          <div className="label-caps mb-3 flex items-center gap-1"><Sparkle size={13} className="brand-text" weight="fill" /> AI Revenue Recommendations</div>
          <div className="grid md:grid-cols-2 gap-3" data-testid="revenue-recs">
            {(d.recommendations || []).map((r, i) => {
              const Icon = KIND_ICON[r.kind] || Sparkle;
              return (
                <div key={i} data-testid={`rec-${i}`} className="card-matte p-4">
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 rounded-lg bg-[#6B3DFF]/12 flex items-center justify-center shrink-0"><Icon size={16} className="brand-text" weight="bold" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm">{r.title}</div>
                      <div className="text-xs text-[#9a9a9a] mt-1">{r.why}</div>
                      <div className="text-[11px] text-emerald-400 mt-1.5 flex items-center gap-1"><TrendUp size={11} /> {r.impact}</div>
                    </div>
                  </div>
                  <Button data-testid={`rec-queue-${i}`} size="sm" onClick={() => act.mutate(r)} disabled={act.isPending}
                    className="mt-3 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white h-8 text-xs">Queue to Approval</Button>
                </div>
              );
            })}
            {!(d.recommendations || []).length && <div className="text-sm text-[#7a7a7a]">No recommendations right now — your catalog looks well optimized.</div>}
          </div>
          <div className="flex items-center gap-2 mt-4 text-xs text-[#7a7a7a]"><ShieldCheck size={14} className="brand-text" /> Recommendations enter the Approval Queue. Nothing changes Shopify or launches ads automatically.</div>
        </>
      )}
    </PageContainer>
  );
}
